import * as React from 'react';

export type SvgIconProps = React.SVGProps<SVGSVGElement> & {
  /** Provides an accessible name when the icon is meaningful on its own. */
  title?: string;
  /**
   * Temporary compatibility for call sites that still pass static MUI-style
   * icon styles. New code should use `className` or `style`.
   */
  sx?: unknown;
};

const toCssStyle = (sx: unknown): React.CSSProperties | undefined => {
  if (!sx || typeof sx !== 'object' || Array.isArray(sx)) {
    return undefined;
  }

  const isCssValue = (value: unknown): value is string | number =>
    typeof value === 'string' || typeof value === 'number';
  const fontSize = 'fontSize' in sx ? sx.fontSize : undefined;
  const width = 'width' in sx ? sx.width : undefined;
  const height = 'height' in sx ? sx.height : undefined;
  const margin = 'm' in sx ? sx.m : undefined;

  return {
    ...(isCssValue(fontSize) ? { fontSize } : {}),
    ...(isCssValue(width) ? { width } : {}),
    ...(isCssValue(height) ? { height } : {}),
    ...(isCssValue(margin) ? { margin } : {})
  };
};

const NativeIcon = React.forwardRef<SVGSVGElement, React.PropsWithChildren<SvgIconProps>>(
  (
    {
      children,
      role,
      style,
      sx,
      title,
      'aria-hidden': ariaHidden,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      ...props
    },
    ref
  ) => {
    const hasAccessibleName = Boolean(title || ariaLabel || ariaLabelledby);

    return (
      <svg
        height='1em'
        width='1em'
        {...props}
        ref={ref}
        aria-hidden={ariaHidden ?? (hasAccessibleName ? undefined : true)}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        role={role ?? (hasAccessibleName ? 'img' : undefined)}
        style={{ ...toCssStyle(sx), ...style }}
        xmlns='http://www.w3.org/2000/svg'
      >
        {title && <title>{title}</title>}
        {children}
      </svg>
    );
  }
);

NativeIcon.displayName = 'NativeIcon';

export default NativeIcon;
